export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

/** Required for any runtime value interpolated into an email body.
 *  `&` first so later entities aren't double-escaped. */
export const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

export const formatNumber = (number: number, locale: string = 'en-US') =>
  new Intl.NumberFormat(locale).format(number)

export const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
}

/** Keep only digits and cap at the 9 digits of a D-U-N-S number. */
export const stripDuns = (value: string) => value.replace(/\D/g, '').slice(0, 9)

/**
 * Format a D-U-N-S number for display as `XX-XXX-XXXX`. Input may contain dashes or be partial
 * (while typing); it is normalized to digits first and formatted as far as it goes. Empty/nullish
 * input returns an empty string.
 */
export const formatDuns = (value: string | null | undefined) => {
  if (!value) return ''
  const digits = stripDuns(value)
  return [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5, 9)].filter(Boolean).join('-')
}

type SnakeToCamel<S extends string> = S extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<SnakeToCamel<Tail>>}`
  : S

export const snakeToCamel = <S extends string>(snake: S): SnakeToCamel<S> => {
  return snake.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()) as SnakeToCamel<S>
}

export const snakeToCamelMap = <const T extends readonly string[]>(
  headers: T,
): Record<T[number], SnakeToCamel<T[number]>> => {
  return headers.reduce(
    (map, snake_case) => {
      const camelCase = snakeToCamel(snake_case)
      map[snake_case as T[number]] = camelCase as SnakeToCamel<T[number]>

      return map
    },
    {} as Record<T[number], SnakeToCamel<T[number]>>,
  )
}

export const toCamelCase = (str: string) => {
  return str
    .split(' ')
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase()
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join('')
}
